import { useMemo } from 'react'

import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import {
  Conversation,
  InboxConversationListItemDto,
  TransactionModel,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { useCacheContext } from '@marketplace-web/messaging/conversation-cache-data'

const CONVERSATION_KEY = 'conversation'
const CONVERSATION_LIST_KEY = 'conversation-list'
const TRANSACTION_KEY = 'transaction'

export const useConversationCache = () => {
  const cache = useCacheContext()
  const isFSEnabled = useFeatureSwitch('conversations_offline_cache')

  return useMemo(() => {
    if (!isFSEnabled) {
      return {
        getCachedConversation: () => Promise.resolve(null),
        setCachedConversation: () => Promise.resolve(),
      }
    }

    const getKey = (id: string | number) => `${CONVERSATION_KEY}:${id}`

    return {
      getCachedConversation: (id: string | number) => cache.get<Conversation>(getKey(id)),
      setCachedConversation: (conversation: Conversation) =>
        cache.set(getKey(conversation.id), conversation),
    }
  }, [cache, isFSEnabled])
}

export const useTransactionCache = () => {
  const cache = useCacheContext()
  const isFSEnabled = useFeatureSwitch('conversations_offline_cache')

  return useMemo(() => {
    if (!isFSEnabled) {
      return {
        getCachedTransaction: () => Promise.resolve(null),
        setCachedTransaction: () => Promise.resolve(),
      }
    }

    const getKey = (id: number) => `${TRANSACTION_KEY}:${id}`

    return {
      getCachedTransaction: (id: number) => cache.get<TransactionModel>(getKey(id)),
      setCachedTransaction: (transaction: TransactionModel) =>
        cache.set(getKey(transaction.id), transaction),
    }
  }, [cache, isFSEnabled])
}

export const useConversationListCache = () => {
  const cache = useCacheContext()
  const isFSEnabled = useFeatureSwitch('conversations_offline_cache')

  return useMemo(() => {
    if (!isFSEnabled) {
      return {
        getCachedConversationList: () => Promise.resolve(null),
        setCachedConversationList: () => Promise.resolve(),
      }
    }

    return {
      getCachedConversationList: () =>
        cache.get<Array<InboxConversationListItemDto>>(CONVERSATION_LIST_KEY),
      setCachedConversationList: (conversations: Array<InboxConversationListItemDto>) =>
        cache.set(CONVERSATION_LIST_KEY, conversations),
    }
  }, [cache, isFSEnabled])
}
